import {
  closestCorners,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {t} from 'i18next';
import {ReactNode, useEffect, useState} from 'react';
import {ReactComponent as UnCheckedIcon} from '../../../../assets/images/active-unchecked.svg';
import {ReactComponent as CheckedIndeterminateIcon} from '../../../../assets/images/icon-checked-indeterminate.svg';
import {ToastType} from '../../../../shared/constants';
import ArrButton from '../../../common/arr-button/ArrButton';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import ArrTransferList, {
  KeyValue,
} from '../../../common/arr-transfer-list/ArrTransferList';
import './SelectSortCodingForms.scss';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {
  setAdditionalPrefs,
  setSelectedCodingFormLists,
} from '../../../../redux/slice/assignmentSlice';

type SecurityGroupCodingFormProps = {
  showDrawer: boolean;
  handleDrawer: (value: React.SetStateAction<boolean>) => void;
};

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
  className?: string;
}

interface ToastMessage {
  error?: string;
  success?: string;
}
interface TabData {
  label: string;
  content: React.ReactNode;
}

const dividerStyle = {
  bgcolor: '#3C4F6B',
  width: '0.6px',
  my: 1,
};

function TabPanel(props: Readonly<TabPanelProps>) {
  const {children, value, index, ...other} = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      data-testid={`select-sort-coding-forms-tab-${index}`}
    >
      {value === index && children}
    </div>
  );
}

export function SortableItem(
  props: Readonly<{
    id: number;
    formData: KeyValue;
    allFormData: KeyValue[];
    isDisabled: boolean;
  }>
) {
  const {attributes, listeners, setNodeRef, transform, transition} =
    useSortable({id: props.id, disabled: props.isDisabled});
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const handleProps = {...listeners, ...attributes};
  return (
    <List
      ref={setNodeRef}
      style={style}
      className="list-item security-group-all-coding-form"
    >
      <ListItem className={`list-item-row`}>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center">
              <ArrIconButton
                icon={<MenuIcon />}
                onClick={(e) => e.stopPropagation()}
                {...(handleProps || {})}
                ariaLabel="dragdrop"
                shape="default"
                size="small"
                testId={`select-sort-coding-forms-dragdrop-icon-button-${props.id}`}
              />
              <Typography
                className={`typo-text body3_size box-sub-heading`}
                noWrap
                data-testid={`select-sort-coding-forms-labels-${props.id}`}
              >
                {props.formData.label}
              </Typography>
            </Box>
          }
        />
      </ListItem>
    </List>
  );
}

const SelectSortCodingForms: React.FC<SecurityGroupCodingFormProps> = ({
  showDrawer,
  handleDrawer,
}) => {
  const [isSubmitDisable, setIsSubmitDisable] = useState<boolean>(true);
  const [isFormSelection, setIsFormSelection] = useState<boolean>(true);
  const [isAllCodingFormSelected, setIsAllCodingFormSelected] =
    useState<boolean>(false);
  const [isNextButtonClicked, setIsNextButtonClicked] =
    useState<boolean>(false);
  const [value, setValue] = useState<number>(0);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<ToastMessage>();
  const [availableAssignedList, setAvailableAssignedList] = useState<
    KeyValue[]
  >([]);
  const [dragableItems, setdragableItems] = useState<KeyValue[]>([]);
  const {codingFormsList} = useSelector(
    (state: RootState) => state.assignment.listData
  );

  const {assignmentModel} = useSelector(
    (state: RootState) => state.assignment.formData
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (codingFormsList && codingFormsList.length) {
      // parse previously saved selection if present
      const parsedSelectedList: Array<{
        codingFormId: number;
        codingFormName?: string;
        isDefault?: boolean;
        sortOrder?: number;
      }> = assignmentModel.selectCodingFormLists
        ? JSON.parse(assignmentModel.selectCodingFormLists)
        : [];

      // Build available list and mark saved default if any
      const available = codingFormsList.map((item) => {
        const saved = parsedSelectedList.find(
          (s) => Number(s.codingFormId) === Number(item.codingFormID)
        );
        return {
          id: item.codingFormID,
          label: item.codingFormName,
          isDefault: !!saved?.isDefault || !!item.isDefault,
        };
      });

      let selected: KeyValue[] = [];

      if (parsedSelectedList && parsedSelectedList.length) {
        // map saved list to actual items (preserve saved sortOrder)
        selected = parsedSelectedList
          .map((s) => {
            const cf = codingFormsList.find(
              (item) => Number(item.codingFormID) === Number(s.codingFormId)
            );
            if (!cf) return null;
            return {
              id: cf.codingFormID,
              label: cf.codingFormName,
              isDefault: !!s.isDefault,
            } as KeyValue;
          })
          .filter((x): x is KeyValue => x !== null)
          // sort by saved sortOrder if present
          .sort((a, b) => {
            const sa =
              parsedSelectedList.find((p) => p.codingFormId === a.id)
                ?.sortOrder ?? 0;
            const sb =
              parsedSelectedList.find((p) => p.codingFormId === b.id)
                ?.sortOrder ?? 0;
            return sa - sb;
          });
      } else {
        // fallback: use codingFormsList.isSelected and item.sortOrder
        selected = codingFormsList
          .filter((item) => item.isSelected)
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((item) => ({
            id: item.codingFormID,
            label: item.codingFormName,
            isDefault: !!item.isDefault,
          }));
      }

      setAvailableAssignedList(available);
      setdragableItems(selected);

      // if all available are selected -> mark all checkbox
      setIsAllCodingFormSelected(
        available.length > 0 &&
          selected.length > 0 &&
          selected.length === available.length
      );
    }
  }, [codingFormsList, assignmentModel]);

  useEffect(() => {
    setIsSubmitDisable(
      !(dragableItems.length && dragableItems.some((x) => x.isDefault))
    );
  }, [dragableItems]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const {active, over} = event;

    if (over && active.id !== over.id) {
      const oldIndex = dragableItems
        .map((item) => item.id)
        .indexOf(Number(active.id));
      const newIndex = dragableItems
        .map((item) => item.id)
        .indexOf(Number(over.id));
      if (oldIndex === 0 || newIndex === 0) {
        return;
      }
      setdragableItems((dragableItems) =>
        arrayMove(dragableItems, oldIndex, newIndex)
      );
    }
  }

  const keepDefaultOnTop = () => {
    const localList = [
      dragableItems.find((item) => item.isDefault) ?? dragableItems[0],
      ...dragableItems.filter((item) => !item.isDefault),
    ];
    setdragableItems(localList);
  };

  const handleAssignChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    user: KeyValue
  ) => {
    const isChecked = event.target.checked;

    setdragableItems((prev) => {
      if (isChecked) {
        const alreadyExists = prev.some((u) => u.id === user.id);
        return alreadyExists ? prev : [...prev, user];
      } else {
        return prev.filter((u) => u.id !== user.id);
      }
    });
  };

  const onChangeAllCodingForm = () => {
    // select / unselect all coding form name
    setdragableItems(isAllCodingFormSelected ? [] : [...availableAssignedList]);
    setIsAllCodingFormSelected(!isAllCodingFormSelected);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (!isMaxCodingFormListExceed()) {
      setValue(newValue);
      if (newValue) {
        // Save
        keepDefaultOnTop();
        setIsFormSelection(false);
      } else {
        setIsFormSelection(true);
      }
    }
  };

  const isMaxCodingFormListExceed = () => {
    if (dragableItems.length > 15) {
      setToastMessage({error: t('codingForms.securityGroup.errorDesc')});
      setShowToast(true);
      return true;
    }
    return false;
  };

  const onClickNextSaveBtn = () => {
    if (isFormSelection) {
      if (!isMaxCodingFormListExceed()) {
        keepDefaultOnTop();
        setValue(1);
        setIsFormSelection(false);
        setIsNextButtonClicked(true);
      }
    } else {
      dispatch(
        setSelectedCodingFormLists(
          JSON.stringify(
            dragableItems.map((item, index) => {
              return {
                codingFormId: item.id,
                codingFormName: item.label,
                isDefault: item.isDefault ?? false,
                sortOrder: index + 1,
              };
            })
          )
        )
      );
      if (dragableItems.length > 1) {
        dispatch(
          setAdditionalPrefs({
            ...assignmentModel.additionalPreferences,
            shouldCompleteOnApplyCodingStamp: false,
          })
        );
      }
      handleDrawer(false);
    }
  };

  const codingFormSelectionBody = (
    <Box>
      <List className="list-item security-group-all-coding-form">
        <ListItem className={`list-item-row`}>
          <ListItemText
            primary={
              <Box className="column-header">
                <Box className="checkbox-with-title">
                  <Checkbox
                    data-testid="select-sort-coding-forms-select-all-checkbox"
                    checked={isAllCodingFormSelected}
                    onChange={() => onChangeAllCodingForm()}
                    className="list-item-checkbox"
                    icon={<UnCheckedIcon />}
                    checkedIcon={<CheckedIndeterminateIcon />}
                  />
                  <Divider orientation="vertical" sx={dividerStyle} flexItem />
                  <Typography
                    className={`typo-text label2_size box-heading`}
                    noWrap
                    data-testid={`select-sort-coding-forms-coding-forms`}
                  >
                    {t('codingForms.securityGroup.codingForms')}
                  </Typography>
                </Box>
                <Box className="text-info-icon">
                  <Typography
                    className={`typo-text label2_size box-heading`}
                    data-testid={`select-sort-coding-forms-set-as-default-required`}
                  >
                    {t('assignment.new.condingFormFlyout.setAsDefaultRequired')}
                  </Typography>
                  <ArrTooltipWrapper
                    title={t(
                      'assignment.new.condingFormFlyout.selectionTooltip.title'
                    )}
                    trigger={
                      <InfoIcon fontSize="medium" className="info-icon" />
                    }
                  >
                    {t(
                      'assignment.new.condingFormFlyout.selectionTooltip.description'
                    )}
                  </ArrTooltipWrapper>
                </Box>
              </Box>
            }
            data-testid="main-top-checkbox"
          />
        </ListItem>
      </List>

      <ArrTransferList
        showCheckbox={true}
        selectedList={dragableItems}
        displayedList={availableAssignedList}
        onChange={handleAssignChange}
        checkboxDataTestId="select-sort-coding-forms-transfer-list-checkbox"
        radioSize="xs"
        showRadio={true}
        onRadioBtnClick={(e) => {
          setAvailableAssignedList(
            availableAssignedList.map((item) => {
              return item.id === e.id
                ? {
                    ...item,
                    isDefault: true,
                  }
                : {
                    ...item,
                    isDefault: false,
                  };
            })
          );
          const localSelectedList = dragableItems.map((item) => {
            return item.id === e.id
              ? {
                  ...item,
                  isDefault: true,
                }
              : {
                  ...item,
                  isDefault: false,
                };
          });
          setdragableItems(localSelectedList);
        }}
      />
    </Box>
  );

  const codingFormOrderBody = (
    <Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragEnd={handleDragEnd}
        modifiers={[]}
      >
        <SortableContext items={dragableItems} strategy={rectSortingStrategy}>
          {dragableItems.map((item) => (
            <SortableItem
              key={item.id}
              id={item.id}
              formData={item}
              allFormData={dragableItems}
              isDisabled={item.isDefault ?? false}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );

  const tabData: TabData[] = [
    {
      label: t('codingForms.securityGroup.codingFormSelection'),
      content: codingFormSelectionBody,
    },
    {
      label: t('codingForms.securityGroup.codingFormOrder'),
      content: codingFormOrderBody,
    },
  ];

  const CustomLoadingOverlay: React.FC = () => {
    return (
      <Box
        className={
          !codingFormsList || codingFormsList?.length === 0
            ? 'loading-overlay'
            : ''
        }
      >
        <ArrLoadingSpinner
          show={!codingFormsList || codingFormsList?.length === 0}
        />
      </Box>
    );
  };

  const contentBody = (
    <Box className="main-container" data-testid="main-container">
      <Box className="header-box">
        <Typography
          className="typo-text headline3_size securityGroup-title"
          data-testid={`select-sort-coding-forms-title`}
        >
          {t('assignment.new.condingFormFlyout.title')}
        </Typography>
        <Box className="action-buttons-box">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={() => {
              handleDrawer(false);
            }}
            disabled={false}
            dataTestId="security-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={isSubmitDisable}
            dataTestId="security-group-save"
            form="security-group-form"
            onClick={onClickNextSaveBtn}
          >
            {isFormSelection ? t('common.next') : t('common.save')}
          </ArrButton>
        </Box>
      </Box>
      <Box className="content-box">
        <div
          className="tab-header"
          data-testid={`select-sort-coding-forms-tabs-wrapper`}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="assign user tabs"
            className="security-group-tabs"
          >
            {tabData.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label}
                className="security-group-tab"
                disabled={
                  index === 1 && (isSubmitDisable || !isNextButtonClicked)
                }
              />
            ))}
          </Tabs>

          <p
            className={`security-group-desc ${!isFormSelection ? 'title-border-bottom' : ''}`}
            data-testid={`select-sort-coding-forms-p`}
          >
            {isFormSelection
              ? t('assignment.new.condingFormFlyout.codingFormSelectionDesc')
              : t('assignment.new.condingFormFlyout.codingFormOrderDesc')}
          </p>

          {tabData.map((tab, index) => (
            <TabPanel key={tab.label} value={value} index={index}>
              {tab.content}
            </TabPanel>
          ))}
        </div>
      </Box>
      <CustomLoadingOverlay />
    </Box>
  );

  return (
    <>
      {showDrawer && (
        <ArrDrawer
          showButton={false}
          showHeader={false}
          isOpen={showDrawer}
          size="small"
          side="right"
          onClose={() => handleDrawer(false)}
          className="select-sort-coding-form-drawer"
        >
          {contentBody}
        </ArrDrawer>
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage?.error ?? toastMessage?.success ?? ''}
          status={toastMessage?.error ? [ToastType.Error] : [ToastType.Success]}
          onClose={() => setShowToast(false)}
          setErrorIconAsAlert
        />
      )}
    </>
  );
};

export default SelectSortCodingForms;
